import { useState } from "react";
import {
  Alert, Box, Paper, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { reportsApiV2, type ArAgingRow, type ApAgingRow } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

/**
 * AR + AP aging — 0-30 / 31-60 / 61-90 / 90+ buckets per customer or vendor.
 * Tabs let the accountant flip between receivables and payables.
 */
export function AgingPage() {
  const [tab, setTab] = useState<"ar" | "ap">("ar");

  const ar = useQuery({ queryKey: ["ar-aging"], queryFn: () => reportsApiV2.arAging(), enabled: tab === "ar" });
  const ap = useQuery({ queryKey: ["ap-aging"], queryFn: () => reportsApiV2.apAging(), enabled: tab === "ap" });

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Aging report</Typography>
      <Paper sx={{ p: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab value="ar" label="Receivable (customers)" />
          <Tab value="ap" label="Payable (vendors)" />
        </Tabs>
        {tab === "ar" && ar.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(ar.error)}</Alert>}
        {tab === "ap" && ap.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(ap.error)}</Alert>}
        {tab === "ar" && (
          <ArTable rows={ar.data ?? []} />
        )}
        {tab === "ap" && (
          <ApTable rows={ap.data ?? []} />
        )}
      </Paper>
    </Box>
  );
}

function ArTable({ rows }: { rows: ArAgingRow[] }) {
  return (
    <Table size="small">
      <TableHead><TableRow>
        <TableCell>Customer</TableCell>
        <TableCell align="right">0–30 d</TableCell>
        <TableCell align="right">31–60 d</TableCell>
        <TableCell align="right">61–90 d</TableCell>
        <TableCell align="right">90+ d</TableCell>
        <TableCell align="right">Total</TableCell>
      </TableRow></TableHead>
      <TableBody>
        {rows.map(r => (
          <TableRow key={r.customerId} hover>
            <TableCell><b>{r.customerName}</b></TableCell>
            <TableCell align="right">{inr.format(r.buckets.bucket0_30)}</TableCell>
            <TableCell align="right">{inr.format(r.buckets.bucket31_60)}</TableCell>
            <TableCell align="right">{inr.format(r.buckets.bucket61_90)}</TableCell>
            <TableCell align="right" sx={{ color: r.buckets.bucketOver90 > 0 ? "error.main" : undefined, fontWeight: 600 }}>
              {inr.format(r.buckets.bucketOver90)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>{inr.format(r.total)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ApTable({ rows }: { rows: ApAgingRow[] }) {
  return (
    <Table size="small">
      <TableHead><TableRow>
        <TableCell>Vendor</TableCell>
        <TableCell align="right">0–30 d</TableCell>
        <TableCell align="right">31–60 d</TableCell>
        <TableCell align="right">61–90 d</TableCell>
        <TableCell align="right">90+ d</TableCell>
        <TableCell align="right">Total</TableCell>
      </TableRow></TableHead>
      <TableBody>
        {rows.map(r => (
          <TableRow key={r.vendorId} hover>
            <TableCell><b>{r.vendorName}</b></TableCell>
            <TableCell align="right">{inr.format(r.buckets.bucket0_30)}</TableCell>
            <TableCell align="right">{inr.format(r.buckets.bucket31_60)}</TableCell>
            <TableCell align="right">{inr.format(r.buckets.bucket61_90)}</TableCell>
            <TableCell align="right" sx={{ color: r.buckets.bucketOver90 > 0 ? "error.main" : undefined, fontWeight: 600 }}>
              {inr.format(r.buckets.bucketOver90)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>{inr.format(r.total)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
